import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Button, 
  Card, 
  Alert, 
  Spinner, 
  Badge, 
  ListGroup 
} from 'react-bootstrap';
import Select from 'react-select';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  getBankDataMappings, 
  saveBankDataMappings, 
  getChildObjects, 
  getChildObjectFields 
} from '../api/bankDataMappings';
import '../styles/FieldMappings.css';

/**
 * Bank Data Mappings page
 * Allows admin users to map bank statement fields to Salesforce related list fields
 */
const BankDataMappings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [mappings, setMappings] = useState([]);
  const [childObjects, setChildObjects] = useState({});
  const [childFields, setChildFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loadingChildObjects, setLoadingChildObjects] = useState({});
  const [loadingChildFields, setLoadingChildFields] = useState({});
  
  // Parent objects available
  const parentObjects = [
    { value: 'Opportunity', label: 'Opportunity' },
    { value: 'Account', label: 'Account' },
    { value: 'Contact', label: 'Contact' }
  ];
  
  // Sort direction options
  const sortDirections = [
    { value: 'DESC', label: 'Descending (Most Recent First)' },
    { value: 'ASC', label: 'Ascending (Oldest First)' }
  ];
  
  // Record position options
  const recordPositions = [
    { value: 0, label: '1st (Most Recent)' },
    { value: 1, label: '2nd' },
    { value: 2, label: '3rd' },
    { value: 3, label: '4th' }
  ];
  
  // Check if user is admin or developer
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'super_admin' && user.role !== 'developer') {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch existing mappings
        const existingMappings = await getBankDataMappings();
        setMappings(existingMappings);
        
        // Load child objects for any parent objects that have mappings
        const uniqueParents = [...new Set(existingMappings.map(m => m.parentObject))];
        for (const parent of uniqueParents) {
          await loadChildObjectsForParent(parent);
        }
        
        // Load child fields for any child objects that have mappings
        const uniqueChildren = [...new Set(existingMappings.filter(m => m.childObject).map(m => m.childObject))];
        for (const child of uniqueChildren) {
          await loadChildFieldsForObject(child);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading bank data mappings:', err);
        setError('Error loading bank data mappings. Please try again.');
        setLoading(false);
      }
    };
    
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Load child objects for a parent
  const loadChildObjectsForParent = async (parentObject) => {
    if (childObjects[parentObject]) return; // Already loaded
    
    setLoadingChildObjects(prev => ({ ...prev, [parentObject]: true }));
    try {
      const children = await getChildObjects(parentObject);
      setChildObjects(prev => ({ ...prev, [parentObject]: children }));
    } catch (err) {
      console.error(`Error loading child objects for ${parentObject}:`, err);
    }
    setLoadingChildObjects(prev => ({ ...prev, [parentObject]: false }));
  };
  
  // Load fields for a child object
  const loadChildFieldsForObject = async (childObject) => {
    if (childFields[childObject]) return; // Already loaded
    
    setLoadingChildFields(prev => ({ ...prev, [childObject]: true }));
    try {
      const fields = await getChildObjectFields(childObject);
      setChildFields(prev => ({ ...prev, [childObject]: fields }));
    } catch (err) {
      console.error(`Error loading fields for ${childObject}:`, err);
    }
    setLoadingChildFields(prev => ({ ...prev, [childObject]: false }));
  };
  
  // Handle parent object change
  const handleParentObjectChange = async (applicationField, selectedOption) => {
    const parentObject = selectedOption?.value || 'Opportunity';
    
    // Update mapping - reset child object and lookup field when parent changes
    setMappings(prev => prev.map(m => 
      m.applicationField === applicationField 
        ? { ...m, parentObject, childObject: null, relationshipName: null, parentLookupField: null, salesforceField: null }
        : m
    ));
    
    // Load child objects if not already loaded
    await loadChildObjectsForParent(parentObject);
  };
  
  // Handle child object change
  const handleChildObjectChange = async (applicationField, selectedOption) => {
    const childObject = selectedOption?.value || null;
    const relationshipName = selectedOption?.relationshipName || null;
    const parentLookupField = selectedOption?.field || null; // The lookup field on child that references parent
    
    // Update mapping with child object and the lookup field
    setMappings(prev => prev.map(m => 
      m.applicationField === applicationField 
        ? { ...m, childObject, relationshipName, parentLookupField, salesforceField: null }
        : m
    ));
    
    // Load child fields if not already loaded
    if (childObject) {
      await loadChildFieldsForObject(childObject);
    }
  };
  
  // Handle salesforce field change
  const handleSalesforceFieldChange = (applicationField, selectedOption) => {
    setMappings(prev => prev.map(m => 
      m.applicationField === applicationField 
        ? { ...m, salesforceField: selectedOption?.value || null }
        : m
    ));
  };
  
  // Handle sort field change
  const handleSortFieldChange = (applicationField, selectedOption) => {
    setMappings(prev => prev.map(m => 
      m.applicationField === applicationField 
        ? { ...m, sortField: selectedOption?.value || null }
        : m
    ));
  };
  
  // Handle sort direction change
  const handleSortDirectionChange = (applicationField, selectedOption) => {
    setMappings(prev => prev.map(m => 
      m.applicationField === applicationField 
        ? { ...m, sortDirection: selectedOption?.value || 'DESC' }
        : m
    ));
  };
  
  // Handle record index change
  const handleRecordIndexChange = (applicationField, selectedOption) => {
    setMappings(prev => prev.map(m => 
      m.applicationField === applicationField 
        ? { ...m, recordIndex: selectedOption?.value ?? 0 }
        : m
    ));
  };
  
  // Save mappings
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      await saveBankDataMappings(mappings);
      
      setSuccess('Bank data mappings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving bank data mappings:', err);
      setError('Error saving bank data mappings. Please try again.');
    }
    setSaving(false);
  };
  
  // Custom styles for Select component
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '32px',
      height: '32px',
      boxShadow: 'none',
      borderColor: '#ced4da',
      fontSize: '0.85rem',
      '&:hover': {
        borderColor: '#80bdff'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: '0.85rem'
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    container: (provided) => ({
      ...provided,
      width: '100%'
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 8px',
      height: '30px'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      margin: '3px 0'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '3px 8px'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '3px 8px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#2e3e9f' : state.isFocused ? '#e0e4ff' : null,
      color: state.isSelected ? 'white' : 'inherit',
      cursor: 'pointer',
      fontSize: '0.85rem',
      padding: '5px 12px'
    })
  };
  
  // Get child object options for a parent
  const getChildObjectOptions = (parentObject) => {
    const children = childObjects[parentObject] || [];
    return [
      { value: null, label: '-- No Related List --' },
      ...children.map(child => ({
        value: child.childObject,
        label: `${child.childObject} (${child.relationshipName})`,
        relationshipName: child.relationshipName,
        field: child.field // The lookup field on child that references parent (e.g., 'csbs__Opportunity__c')
      }))
    ];
  };
  
  // Get field options for a child object
  const getFieldOptions = (childObject) => {
    const fields = childFields[childObject] || [];
    return [
      { value: null, label: '-- Select Field --' },
      ...fields.map(field => ({
        value: field.name,
        label: `${field.label} (${field.name})`
      }))
    ];
  };
  
  // Check if mapping is complete
  const isMappingComplete = (mapping) => {
    return mapping.childObject && mapping.parentLookupField && mapping.salesforceField && mapping.sortField;
  };
  
  // Count complete mappings
  const completeMappingsCount = mappings.filter(isMappingComplete).length;
  
  if (loading) {
    return (
      <Container className="mt-4">
        <Card>
          <Card.Body className="text-center p-5">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <h5>Loading Bank Data Mappings...</h5>
            <p className="text-muted">
              Retrieving existing mappings and Salesforce metadata.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }
  
  return (
    <Container fluid className="mt-4 px-4">
      <Row className="mb-4">
        <Col>
          <h2>Bank Statement Data Mappings</h2>
          <p className="text-muted">
            Map bank statement fields to Salesforce related list objects. These mappings allow the system to pull 
            monthly bank statement data from Salesforce Statement records.
          </p>
        </Col>
      </Row>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
      )}
      
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>
      )}
      
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <strong>Field Mappings</strong>
            <Badge bg={completeMappingsCount === mappings.length ? 'success' : 'warning'} className="ms-2">
              {completeMappingsCount}/{mappings.length} Configured
            </Badge>
          </div>
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving...</> : 'Save Mappings'}
          </Button>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-4">
            <strong>How it works:</strong>
            <ol className="mb-0 mt-2">
              <li>Select the <strong>Parent Object</strong> (usually Opportunity)</li>
              <li>Select the <strong>Related List</strong> (e.g., Statement__c)</li>
              <li>Select the <strong>Salesforce Field</strong> to pull data from</li>
              <li>Configure the <strong>Sort Field</strong> to determine record order (e.g., Statement_End_Date__c)</li>
              <li>The <strong>Record Position</strong> determines which record maps to Month 1, Month 2, etc.</li>
            </ol>
          </Alert>
          
          <ListGroup>
            {mappings.map((mapping) => {
              const isComplete = isMappingComplete(mapping);
              
              return (
                <ListGroup.Item 
                  key={mapping.applicationField}
                  className={isComplete ? 'border-success' : ''}
                  style={{ backgroundColor: isComplete ? '#f8fff8' : 'white' }}
                >
                  <Row className="align-items-center">
                    <Col md={2}>
                      <strong>{mapping.fieldLabel}</strong>
                      <div className="text-muted small">{mapping.applicationField}</div>
                      <Badge bg={mapping.fieldType === 'currency' ? 'success' : 'secondary'} className="mt-1">
                        {mapping.fieldType}
                      </Badge>
                    </Col>
                    
                    <Col md={2}>
                      <Form.Label className="small mb-1">Parent Object</Form.Label>
                      <Select
                        value={parentObjects.find(p => p.value === mapping.parentObject)}
                        onChange={(option) => handleParentObjectChange(mapping.applicationField, option)}
                        options={parentObjects}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                        isSearchable={true}
                      />
                    </Col>
                    
                    <Col md={2}>
                      <Form.Label className="small mb-1">Related List</Form.Label>
                      <Select
                        value={mapping.childObject ? {
                          value: mapping.childObject,
                          label: mapping.childObject
                        } : null}
                        onChange={(option) => handleChildObjectChange(mapping.applicationField, option)}
                        options={getChildObjectOptions(mapping.parentObject)}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                        isSearchable={true}
                        isLoading={loadingChildObjects[mapping.parentObject]}
                        placeholder="Select related list..."
                      />
                    </Col>
                    
                    <Col md={2}>
                      <Form.Label className="small mb-1">Salesforce Field</Form.Label>
                      <Select
                        value={mapping.salesforceField ? {
                          value: mapping.salesforceField,
                          label: mapping.salesforceField
                        } : null}
                        onChange={(option) => handleSalesforceFieldChange(mapping.applicationField, option)}
                        options={mapping.childObject ? getFieldOptions(mapping.childObject) : []}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                        isSearchable={true}
                        isLoading={loadingChildFields[mapping.childObject]}
                        isDisabled={!mapping.childObject}
                        placeholder="Select field..."
                      />
                    </Col>
                    
                    <Col md={2}>
                      <Form.Label className="small mb-1">Sort By</Form.Label>
                      <Select
                        value={mapping.sortField ? {
                          value: mapping.sortField,
                          label: mapping.sortField
                        } : null}
                        onChange={(option) => handleSortFieldChange(mapping.applicationField, option)}
                        options={mapping.childObject ? getFieldOptions(mapping.childObject) : []}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                        isSearchable={true}
                        isDisabled={!mapping.childObject}
                        placeholder="Sort field..."
                      />
                      <Select
                        value={sortDirections.find(d => d.value === mapping.sortDirection)}
                        onChange={(option) => handleSortDirectionChange(mapping.applicationField, option)}
                        options={sortDirections}
                        styles={{...customStyles, control: (provided) => ({...customStyles.control(provided), marginTop: '4px'})}}
                        menuPortalTarget={document.body}
                        isDisabled={!mapping.childObject}
                      />
                    </Col>
                    
                    <Col md={2}>
                      <Form.Label className="small mb-1">Record Position</Form.Label>
                      <Select
                        value={recordPositions.find(r => r.value === mapping.recordIndex)}
                        onChange={(option) => handleRecordIndexChange(mapping.applicationField, option)}
                        options={recordPositions}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                        isDisabled={!mapping.childObject}
                      />
                      {isComplete && (
                        <Badge bg="success" className="mt-2">✓ Configured</Badge>
                      )}
                    </Col>
                  </Row>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Card.Body>
        <Card.Footer>
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving...</> : 'Save Mappings'}
          </Button>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default BankDataMappings;

